/**
 * GradientButton - Button component with gradient backgrounds and glow effects
 *
 * Supports multiple variants (primary, accent, success, danger), three sizes (sm, md, lg),
 * optional glow effects on hover, and loading states with spinner.
 */

import { ButtonHTMLAttributes } from "react";

interface GradientButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "accent" | "success" | "danger";
  size?: "sm" | "md" | "lg";
  glow?: boolean;
  isLoading?: boolean;
}

export default function GradientButton({
  variant = "primary",
  size = "md",
  glow = false,
  isLoading = false,
  disabled,
  children,
  className = "",
  ...props
}: GradientButtonProps) {
  const variantClasses = {
    primary: "bg-gradient-primary",
    accent: "bg-gradient-accent",
    success: "bg-gradient-success",
    danger: "bg-gradient-danger",
  };

  const sizeClasses = {
    sm: "h-9 px-4 text-sm",
    md: "h-11 px-6 text-base",
    lg: "h-14 px-8 text-lg",
  };

  const glowClasses = glow
    ? {
        primary: "hover:shadow-glow-pink-lg",
        accent: "hover:shadow-glow-purple-lg",
        success: "hover:shadow-glow-green",
        danger: "hover:shadow-glow-pink-lg",
      }[variant]
    : "";

  const isDisabled = disabled || isLoading;

  return (
    <button
      disabled={isDisabled}
      className={`
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${glowClasses}
        relative
        rounded-lg
        font-semibold
        text-white
        transition-all
        duration-300
        focus:outline-none
        focus:ring-2
        focus:ring-pink-500
        focus:ring-offset-2
        focus:ring-offset-gray-900
        ${
          isDisabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:scale-[1.02] active:scale-95"
        }
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      )}
      <span className={`inline-flex items-center ${isLoading ? "invisible" : ""}`}>{children}</span>
    </button>
  );
}
